import { Box, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { ISubmissionStageStatusSetting, saveSubmissionStageStatusSetting } from 'wisestep-api';
import {
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
  WsButton,
} from 'wisestep-base';

import {
  getCandidateStageData,
  getStageStatusSettings,
} from '../../../utils/CandidateStageStatusHelper';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { SubmissionType } from '../../conversationWidget/types/SubmissionTypes';
import { useSubmissionStageStatus } from '../hooks/useSubmissionStageStatus';

const submissionTypes = Object.values(SubmissionType).map((type) => ({
  label: type,
  value: type,
}));

const setSubmissionValues = (data: ISubmissionStageStatusSetting[], setValue: any) => {
  data.forEach((item) => {
    const index = submissionTypes.findIndex((st) => st.value === item.submissionType);
    if (index !== -1) {
      setValue(`submissions.${index}.id`, item.id);
      setValue(`submissions.${index}.workspaceId`, item.workspaceId);
      setValue(`submissions.${index}.applicationStage`, item.applicationStage);
      setValue(`submissions.${index}.applicationState`, item.applicationState);
    }
  });
};

const SubmissionStageStatusSettings = () => {
  const [stageOptions, setStageOptions] = useState<WsSelectObj[]>([]);
  const [statusOptions, setStatusOptions] = useState<Record<string, WsSelectObj[]>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { submissionStageData } = useSubmissionStageStatus();

  const methods = useForm<{ submissions: ISubmissionStageStatusSetting[] }>({
    defaultValues: {
      submissions: submissionTypes.map((type) => ({
        id: '',
        workspaceId: '',
        submissionType: type.value,
        applicationStage: '',
        applicationState: '',
      })),
    },
  });
  const { control, handleSubmit, watch, setValue } = methods;
  const { fields } = useFieldArray({ control, name: 'submissions' });

  const fetchStageSettings = async () => {
    try {
      const response = await getStageStatusSettings();
      const stageData = getCandidateStageData(response?.data) || [];

      setStageOptions(stageData.map((stage) => ({ value: stage.stage, label: stage.stage })));

      const statusMap: Record<string, WsSelectObj[]> = {};
      stageData.forEach((stage) => {
        statusMap[stage.stage] = stage.statuses
          .filter((status) => status.status === 'active')
          .map((status) => ({ value: status.value, label: status.value }));
      });

      setStatusOptions(statusMap);
    } catch (error) {
      console.error('Error fetching stage settings:', error);
      setNotification(failed('Failed to fetch stage status settings'));
    }
  };

  useEffect(() => {
    fetchStageSettings();
  }, []);

  useEffect(() => {
    if (submissionStageData.length > 0) {
      setSubmissionValues(submissionStageData, setValue);
    }
  }, [submissionStageData]);

  const onFormSubmit = async (data: { submissions: ISubmissionStageStatusSetting[] }) => {
    setSaving(true);
    try {
      const payload = data.submissions.map(
        ({ id, workspaceId, submissionType, applicationStage, applicationState }) => ({
          id,
          workspaceId,
          submissionType,
          applicationStage,
          applicationState,
        })
      );

      const res = await saveSubmissionStageStatusSetting(payload);
      if (res?.data) {
        setNotification(success('Submission stage status saved successfully'));
        setSubmissionValues(res.data, setValue);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      setNotification(failed('Failed to save submission stage status'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box p={3}>
        <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK} bold>
          Customise stage and status for submission types
        </WsTypography>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container pt={3} spacing={2}>
            {fields.map((field, index) => {
              const selectedStage = watch(`submissions.${index}.applicationStage`);

              return (
                <Grid container item spacing={2} key={field.id}>
                  <Grid item xs={4}>
                    <WsSelect
                      name={`submissions.${index}.submissionType`}
                      label="Submission Type"
                      selectArray={submissionTypes}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <WsSelect
                      name={`submissions.${index}.applicationStage`}
                      label="Stage"
                      selectArray={stageOptions}
                      required
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <WsSelect
                      name={`submissions.${index}.applicationState`}
                      label="Status"
                      selectArray={statusOptions[selectedStage || ''] || []}
                      required
                    />
                  </Grid>
                </Grid>
              );
            })}

            <Grid container justifyContent="flex-end" item xs={12} pt={3}>
              <WsButton type="submit" size="large" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </WsButton>
            </Grid>
          </Grid>
        </form>
      </Box>
    </FormProvider>
  );
};

export default SubmissionStageStatusSettings;
